import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || '/erp/api/v1';

export const normalizeApiPath = (url = '') => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/erp/api/v1/')) return url.slice('/erp/api/v1'.length);
  if (url === '/erp/api/v1') return '';
  if (url.startsWith('/api/v1/')) return url.slice('/api/v1'.length);
  if (url === '/api/v1') return '';
  return url.startsWith('/') ? url : `/${url}`;
};

export const ensureAuthToken = async () => {
  return localStorage.getItem('nexus_token') || '';
};

// API instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    const token = await ensureAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('nexus_token');
      if (!window.location.pathname.startsWith('/erp/login')) {
        window.location.assign('/erp/login');
      }
    }
    return Promise.reject(error);
  }
);

// Hook for API calls
export const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (method, url, data = null, config = {}) => {
    setLoading(true);
    setError(null);
    try {
      const path = normalizeApiPath(url);
      const response = ['get', 'delete'].includes(method)
        ? await api[method](path, config)
        : await api[method](path, data, config);
      return { success: true, data: response.data };
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      return { success: false, error: err.response?.data?.message || err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((url, config) => request('get', url, null, config), [request]);
  const post = useCallback((url, data, config) => request('post', url, data, config), [request]);
  const put = useCallback((url, data, config) => request('put', url, data, config), [request]);
  const patch = useCallback((url, data, config) => request('patch', url, data, config), [request]);
  const del = useCallback((url, config) => request('delete', url, null, config), [request]);

  return { get, post, put, patch, del, loading, error };
};

// Hook for data fetching with caching
export const useFetch = (url, options = {}) => {
  const { immediate = true, initialData = null } = options;
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(normalizeApiPath(url));
      setData(response.data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (immediate) {
      fetch();
    }
  }, [immediate, fetch]);

  return { data, loading, error, refetch: fetch, setData };
};

// Hook for mutations
export const useMutation = (method) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const mutate = useCallback(async (url, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api[method](normalizeApiPath(url), data);
      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [method]);

  return { mutate, loading, error };
};

export { api };
export default useApi;
